/**
 * GPT direto no GAM da Grumft, no lugar dos scripts api.grumft.com/gt e /stick.
 * Repete o que o script deles fazia: initial load desligado + single request, 1ª chamada no
 * ad unit normal, vazio tenta de novo até 3 vezes (1 s), e da 2ª chamada em diante vai para
 * o `_R` quando ele existe.
 */
import { GAM_NETWORK_CODE } from './grumft-iab-presets';

export const GPT_SRC = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';

const EMPTY_RETRY_DELAY_MS = 1000;
const MAX_EMPTY_RETRIES = 3;

/** @type {Promise<void> | null} */
let gptReady = null;
let servicesReady = false;
let divSeq = 0;
/** div id → callbacks do slot que está nele */
const slotHandlers = new Map();

function getGoogletag() {
  window.googletag = window.googletag || { cmd: [] };
  window.googletag.cmd = window.googletag.cmd || [];
  return window.googletag;
}

/** Carrega o gpt.js uma vez. Rejeita se o script não carregar (bloqueador, rede). */
export function loadGpt() {
  if (gptReady) return gptReady;
  const googletag = getGoogletag();
  gptReady = new Promise((resolve, reject) => {
    googletag.cmd.push(() => resolve());
    if (document.querySelector(`script[src="${GPT_SRC}"]`)) return;
    const script = document.createElement('script');
    script.async = true;
    script.src = GPT_SRC;
    script.setAttribute('data-ad-deferred', '1');
    script.onerror = () => {
      script.remove();
      gptReady = null;
      reject(new Error('GPT: script falhou'));
    };
    document.head.appendChild(script);
  });
  return gptReady;
}

function setUpServices(googletag) {
  if (servicesReady) return;
  servicesReady = true;
  const pubads = googletag.pubads();
  pubads.disableInitialLoad();
  pubads.enableSingleRequest();
  pubads.addEventListener('slotRenderEnded', (event) => {
    slotHandlers.get(event.slot.getSlotElementId())?.onRenderEnded(event);
  });
  pubads.addEventListener('impressionViewable', (event) => {
    slotHandlers.get(event.slot.getSlotElementId())?.onViewable(event);
  });
  googletag.enableServices();
}

/**
 * Mostra um ad unit dentro de `container` (caixa do tamanho do anúncio, centralizada).
 * @param {HTMLElement} container
 * @param {{ adUnit: string, refreshAdUnit?: string, width: number, height: number, sizes: number[][], refreshSeconds?: number }} zone
 * @returns {{ loaded: Promise<void>, destroy: () => void }}
 */
export function showGamSlot(container, zone) {
  const { adUnit, refreshAdUnit, width, height, sizes, refreshSeconds = 0 } = zone;
  const box = document.createElement('div');
  box.className = 'gam-ad-box';
  box.style.width = `${width}px`;
  box.style.height = `${height}px`;
  container.appendChild(box);

  let slot = null;
  let slotDiv = null;
  let requests = 0;
  let emptyRetries = 0;
  let timer;
  let stopped = false;

  const request = () => {
    if (stopped) return;
    const googletag = getGoogletag();
    googletag.cmd.push(() => {
      if (stopped) return;
      setUpServices(googletag);
      if (slot) {
        slotHandlers.delete(slotDiv.id);
        googletag.destroySlots([slot]);
      }
      divSeq += 1;
      const div = document.createElement('div');
      div.id = `gam-${adUnit}-${divSeq}`;
      if (slotDiv) slotDiv.replaceWith(div);
      else box.appendChild(div);
      slotDiv = div;

      const unit = requests > 0 && refreshAdUnit ? refreshAdUnit : adUnit;
      slot = googletag.defineSlot(`/${GAM_NETWORK_CODE}/${unit}`, sizes, div.id);
      if (!slot) return;
      slot.addService(googletag.pubads());
      slotHandlers.set(div.id, { onRenderEnded, onViewable });
      googletag.display(div.id);
      googletag.pubads().refresh([slot]);
      requests += 1;
    });
  };

  function onRenderEnded(event) {
    if (!event.isEmpty || emptyRetries >= MAX_EMPTY_RETRIES) return;
    emptyRetries += 1;
    clearTimeout(timer);
    timer = setTimeout(request, EMPTY_RETRY_DELAY_MS);
  }

  function onViewable() {
    if (!refreshSeconds) return;
    clearTimeout(timer);
    timer = setTimeout(request, refreshSeconds * 1000);
  }

  const loaded = loadGpt();
  loaded.then(request, () => {});

  const destroy = () => {
    stopped = true;
    clearTimeout(timer);
    if (slotDiv) slotHandlers.delete(slotDiv.id);
    const current = slot;
    if (current && window.googletag?.cmd) {
      window.googletag.cmd.push(() => window.googletag.destroySlots([current]));
    }
    box.remove();
  };

  return { loaded, destroy };
}
